import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import { Menu } from 'antd';
import randomColor from 'randomcolor';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  selectAccountOrgs,
  selectActiveUser,
  selectIsAdmin,
  selectShowOrgSelector,
} from 'shared/store/auth-slice';
import { Org } from 'types/user';
import DefaultOrgLogoIcon from 'shared/assets/images/default-org-logo-icon.svg';
import { CreateOrgModal } from './CreateOrgModal';
import { NavHoverMenu } from '../NavHoverMenu';
import { changeActiveOrg } from 'shared/store/shared-actions';
import './style.less';

export const OrgSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const showOrgSelector = useAppSelector(selectShowOrgSelector);
  const isAdmin = useAppSelector(selectIsAdmin);
  const user = useAppSelector(selectActiveUser);
  const orgs = useAppSelector(selectAccountOrgs);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCreateOrgOpen, setIsCreateOrgOpen] = useState(false);

  const handleOpenMenu = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  const handleOpenCreateOrg = useCallback(() => {
    setIsCreateOrgOpen(true);
  }, []);

  const handleCloseCreateOrg = useCallback(() => {
    setIsCreateOrgOpen(false);
  }, []);

  const handleSelectOrg = useCallback(
    (org: Org) => {
      history.push(`/`);
      dispatch(changeActiveOrg(org.id));
    },
    [dispatch, history],
  );

  if (!user || !showOrgSelector) {
    return null;
  }

  if (!isAdmin && orgs.length === 1) {
    return null;
  }

  return (
    <>
      <div
        className="OrgSelector"
        onMouseEnter={handleOpenMenu}
        onFocus={handleOpenMenu}
        onMouseLeave={handleCloseMenu}
        onBlur={handleCloseMenu}
      >
        <button className="OrgSelector__button">
          <OrgSelectorIcon org={user.org} />
        </button>
        <NavHoverMenu isOpen={isMenuOpen}>
          <Menu selectedKeys={[user.org.id.toString()]}>
            {orgs.map(org => (
              <Menu.Item key={org.id.toString()} onClick={() => handleSelectOrg(org)}>
                <OrgSelectorItem org={org} />
              </Menu.Item>
            ))}
            {isAdmin && (
              <>
                <Menu.Divider />
                <Menu.Item
                  className="OrgSelector__menu-create"
                  onClick={handleOpenCreateOrg}
                >
                  + Create new organization
                </Menu.Item>
              </>
            )}
          </Menu>
        </NavHoverMenu>
      </div>
      <CreateOrgModal isVisible={isCreateOrgOpen} onClose={handleCloseCreateOrg} />
    </>
  );
};

interface OrgSelectorItemProps {
  org: Org;
}

const OrgSelectorItem: React.FC<OrgSelectorItemProps> = ({ org }) => {
  return (
    <div className="OrgSelectorItem">
      <OrgSelectorIcon org={org} menuItem />
      <div className="OrgSelectorItem__name">
        {org.name || <em>Unnamed organization</em>}
      </div>
    </div>
  );
};

export const OrgSelectorIcon: React.FC<{ org: Org; menuItem?: boolean }> = ({
  org,
  menuItem,
}) => {
  const color = randomColor({ seed: org.id, luminosity: 'dark' });
  return (
    <div
      className={clsx('OrgSelectorIcon', menuItem && 'OrgSelectorIcon--menuItem')}
      style={{ color }}
    >
      <DefaultOrgLogoIcon />
    </div>
  );
};
